import React from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import CategoryCard from '../../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../../common/category-card/types';
import TerminationIcon from '../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../assets/svg/policies_and_procedures.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import DocumentsBlock, { DocumentsActions } from '../../../common/documents-block/DocumentsBlock';
import styles from '../EmployeeProfile.module.scss';
import RecruitmentIcon from '../../../../assets/svg/recruitment.svg?react';
import AgreementIcon from '../../../../assets/svg/policy.svg?react';
import NewEmployeeManualIcon from '../../../../assets/svg/new-employee-manual.svg?react';
import OnboardingIcon from '../../../../assets/svg/probation.svg?react';
import ProbationIcon from '../../../../assets/svg/probation.svg?react';
import EmployeeManagementIcon from '../../../../assets/svg/employee-management.svg?react';
import PerformanceManagementIcon from '../../../../assets/svg/performance.svg?react';
import ManagingUnderperformanceIcon from '../../../../assets/svg/growth.svg?react';
import { useLocation } from 'react-router-dom';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { clearDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import BadgeIcon from '../../../../assets/svg/badge.svg?react';

export const EmploymentCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
  cardView,
  documentsActions,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Extract profileId from pathname
  const profileId = location.pathname.split('/employee-profiles/')[1]?.split('/')[0];

  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);

  // Use metadata hook with profileId
  const { uploadedDocumentsMetaData } = useDocumentsMetaData(
    'All',
    'employment',
    Number(profileId)
  );

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return <RecruitmentIcon />;
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return <AgreementIcon />;
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return <NewEmployeeManualIcon />;
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return <OnboardingIcon />;
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return <EmployeeManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return <ProbationIcon />;
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return <PerformanceManagementIcon />;
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return <ManagingUnderperformanceIcon />;
      case t('tagsLabels.documentWorkflow.employment.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return <TerminationIcon />;
      case t('tagsLabels.documentWorkflow.employment.generalAdministration'):
        return <OperationsIcon />;
      case t('tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses'):
        return <BadgeIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.employment.recruitment'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.employment.agreement'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.employment.newEmployeeManual'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.employment.policiesAndProcedures'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.employment.induction'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.employment.employeeManagement'):
        return '6. ';
      case t('tagsLabels.documentWorkflow.employment.probation'):
        return '7. ';
      case t('tagsLabels.documentWorkflow.employment.performanceManagement'):
        return '8. ';
      case t('tagsLabels.documentWorkflow.employment.managingUnderperformance'):
        return '9. ';
      case t('tagsLabels.documentWorkflow.employment.termination'):
        return '10. ';
      case t('tagsLabels.documentWorkflow.employment.accounting'):
        return '11. ';
      case t('tagsLabels.documentWorkflow.employment.generalAdministration'):
        return '12. ';
      case t('tagsLabels.documentWorkflow.employment.idCardsPassportsAndLicenses'):
        return '13. ';
    }
  };

  return (
    <>
      {/* Workflows subheader + documents actions (workflow cards view) in one row. */}
      {((!workflow && !showWorkflowBreadcrumbs) ||
        (documentsActions && !(workflow && showWorkflowBreadcrumbs))) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          {!workflow && !showWorkflowBreadcrumbs && (
            <Typography
              variant='h3'
              component='h3'
              sx={{ margin: 0, fontSize: '15px', lineHeight: '24px' }}
            >
              {t('headers.workflows')}
            </Typography>
          )}
          {documentsActions && !(workflow && showWorkflowBreadcrumbs) && (
            <DocumentsActions {...documentsActions} standalone />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
          paddingBottom: '24px',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                id='employeeProfilesEmploymentCategoryContentWorkflowsLink'
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setWorkflow(null);
                  setShowWorkflowBreadcrumbs(false);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            {/* Open workflow: the documents actions are attached to the table. */}
            <Box style={{ position: 'relative', width: '100%' }}>
              <DocumentsBlock actions={documentsActions}>
                <DocumentListVault
                  cardView={cardView}
                  compact
                  embedded={!!documentsActions}
                  documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
                />
                {documentUploadsLoading === 'loading' && <ProgressOverlay position='absolute' />}
              </DocumentsBlock>
            </Box>
          </>
        ) : (
          <CategoryCardGrid id='employeeProfilesEmploymentCategoryContentGrid'>
            {TAGS_LABELS.documentWorkflow.employment.map((el) => (
              <CategoryCard
                id={`employeeProfilesEmploymentCategoryContentEmploymentCard_${el}`}
                title={getWorkflowNumber(t(el)) + t(el)}
                countLabel={t(
                  getDocsCount(t(el)) === 1
                    ? 'categoryCards.documentCountSingular'
                    : 'categoryCards.documentCountPlural',
                  { count: getDocsCount(t(el)) }
                )}
                accent={CATEGORY_CARD_ACCENTS.Employment}
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.employment.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </CategoryCardGrid>
        )}
      </Box>
    </>
  );
};
